import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconCarouselArrows,
  ResponsiveImage,
  IconViewGallery,
  MasonryPopup
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
      ? { aspectWidth: 4, aspectHeight: 3 }
      : hasDimensions
        ? { aspectWidth: w, aspectHeight: h }
        : { aspectWidth: 1, aspectHeight: 1 };
};

const handleSlide = index => {
  const wrapper = document.querySelector(
    '.image-gallery-thumbnails-wrapper'
  );
  const container = document.querySelector(
    '.image-gallery-thumbnails-container'
  );
  const thumbnails = document.querySelectorAll(
    '.image-gallery-thumbnail'
  );

  if (!wrapper || !container || !thumbnails.length) return;

  const wrapperWidth = wrapper.offsetWidth;
  const thumbWidth = thumbnails[0].offsetWidth;

  const computedStyle = window.getComputedStyle(container);
  const gap = parseInt(computedStyle.columnGap || computedStyle.gap || 0, 10);

  const fullThumbWidth = thumbWidth + gap;

  const visibleCount = Math.floor(wrapperWidth / fullThumbWidth);
  const page = Math.floor(index / visibleCount);

  const offset = page * wrapperWidth;

  container.style.transition = 'transform 450ms ease-out';
  container.style.transform = `translate3d(-${offset}px, 0, 0)`;
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intl = useIntl();
  const {
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
    onManageDisableScrolling,
    listingTitle
  } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    const index = i < 9 ? `0${i + 1}` : i + 1
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
      index
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    const hasScaledXlarge = item?.image?.attributes?.variants?.["scaled-xlarge"];

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            // variants={imageVariants}
            variants={hasScaledXlarge ? ["scaled-xlarge"] : imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div className={css.customDot}>
        {item.index}
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconCarouselArrows type="white-left" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconCarouselArrows type="white-right" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    // return isFullscreen ? (
    //   <Button
    //     onClick={onClick}
    //     rootClassName={css.close}
    //     title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
    //   >
    //     <span className={css.closeText}>
    //       <FormattedMessage id="ListingImageGallery.closeModal" />
    //     </span>
    //     <IconClose rootClassName={css.closeIcon} />
    //   </Button>
    // ) : (
    //   <button className={css.openFullscreen} onClick={onClick}>
    //     <IconViewGallery />
    //     <FormattedMessage id="ListingPage.viewGallery" />
    //   </button>
    // );

    return (
      <MasonryPopup
        id="primaryImagesGallery"
        items={images}
        buttonClass={css.openFullscreen}
        buttonText={<FormattedMessage id="ListingPage.viewGallery" />}
        onManageDisableScrolling={onManageDisableScrolling}
        iconType="gallery"
        listingTitle={listingTitle}
      />
    )
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      onSlide={handleSlide}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
